import React, { useState, useRef, useEffect } from 'react';
import './ABCDExercise.css';

const ABCDExercise = ({ questions, title, level, passage = '', onClose, updateAttributes, node, editor, getPos, onUpdate, onDelete, isBoard, isStudent = false, myState = {}, otherState = {}, updateState = () => {} }) => {
  const [selectedAnswers, setSelectedAnswers] = useState({});
  const [showResults, setShowResults] = useState(false);
  const [score, setScore] = useState(0);
  const syncedRef = React.useRef(false);
  useEffect(() => {
    if (syncedRef.current) return;
    const hasAnswers = myState.answers && Object.keys(myState.answers).length > 0;
    const hasResults = myState.showResults === true;
    if (hasAnswers || hasResults) {
      syncedRef.current = true;
      if (myState.answers) setSelectedAnswers(myState.answers);
      if (myState.showResults) setShowResults(true);
      if (typeof myState.score === 'number') setScore(myState.score);
      if (typeof myState.currentQuestion === 'number') setCurrentQuestionIndex(myState.currentQuestion);
    }
  }, [myState.answers, myState.showResults, myState.score, myState.currentQuestion]); // eslint-disable-line react-hooks/exhaustive-deps

  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [isEditing, setIsEditing] = useState(false);
  const [editableQuestions, setEditableQuestions] = useState([...questions]);
  const [editableTitle, setEditableTitle] = useState(title);
  const [editableLevel, setEditableLevel] = useState(level);
  // Source passage: shown to the student in a collapsible panel above the
  // questions, and editable by the teacher in edit mode via textarea.
  const [editablePassage, setEditablePassage] = useState(passage || '');
  const [passageExpanded, setPassageExpanded] = useState(false);
  const addBtnRef = useRef(null);
  const dotsScrollRef = useRef(null);
  const hintRunRef = useRef(false);
  const prevResetAtRef = useRef(null);
  const [showLeftFade, setShowLeftFade] = useState(false);
  const [showRightFade, setShowRightFade] = useState(false);

  // Follow the other side's navigation
  useEffect(() => {
    if (typeof otherState.currentQuestion === 'number') {
      setCurrentQuestionIndex(otherState.currentQuestion);
    }
  }, [otherState.currentQuestion]); // eslint-disable-line react-hooks/exhaustive-deps

  // When other side submits, show results locally too
  useEffect(() => {
    if (otherState.showResults === true && !showResults) {
      let correctCount = 0;
      editableQuestions.forEach((question, index) => {
        const correctOption = question.options.find(opt => opt.isCorrect);
        if (selectedAnswers[index] === correctOption?.id) correctCount++;
      });
      setScore(correctCount);
      setShowResults(true);
    }
  }, [otherState.showResults]); // eslint-disable-line react-hooks/exhaustive-deps

  // When other side resets, reset locally too
  useEffect(() => {
    if (!otherState.resetAt) return;
    if (prevResetAtRef.current === otherState.resetAt) return;
    prevResetAtRef.current = otherState.resetAt;
    setSelectedAnswers({});
    setShowResults(false);
    setScore(0);
    setCurrentQuestionIndex(0);
    syncedRef.current = false;
  }, [otherState.resetAt]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (hintRunRef.current) return;
    if (!dotsScrollRef.current) return;
    if (editableQuestions.length <= 5) return;
    // Не в режиме редактирования/результатов, мягкий намёк один раз
    hintRunRef.current = true;
    const scroller = dotsScrollRef.current;
    const delta = 24; // px
    try {
      const start = scroller.scrollLeft;
      const t1 = setTimeout(() => {
        scroller.scrollTo({ left: start + delta, behavior: 'smooth' });
        const t2 = setTimeout(() => {
          scroller.scrollTo({ left: start, behavior: 'smooth' });
        }, 500);
        // cleanup inner
        const cleanup = () => clearTimeout(t2);
        // store cleanup on ref for outer return
        (hintRunRef.currentCleanup = cleanup);
      }, 120);
      return () => {
        clearTimeout(t1);
        if (hintRunRef.currentCleanup) hintRunRef.currentCleanup();
      };
    } catch (_) {
      // no-op
    }
  }, [editableQuestions.length]);

  // Update gradient fades based on scroll position
  useEffect(() => {
    const el = dotsScrollRef.current;
    if (!el) return;
    const update = () => {
      const max = el.scrollWidth - el.clientWidth;
      setShowLeftFade(el.scrollLeft > 2);
      setShowRightFade(el.scrollLeft < max - 2);
    };
    update();
    el.addEventListener('scroll', update, { passive: true });
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => {
      el.removeEventListener('scroll', update);
      ro.disconnect();
    };
  }, []);

  const handleAnswerSelect = (questionIndex, optionId) => {
    if (showResults || isEditing) return;
    setSelectedAnswers(prev => {
      const next = { ...prev, [questionIndex]: optionId };
      updateState({ answers: next });
      return next;
    });
  };

  const handleSubmit = () => {
    let correctCount = 0;
    editableQuestions.forEach((question, index) => {
      const selectedAnswer = selectedAnswers[index];
      const correctOption = question.options.find(opt => opt.isCorrect);
      if (selectedAnswer === correctOption.id) {
        correctCount++;
      }
    });
    setScore(correctCount);
    setShowResults(true);
    updateState({ answers: selectedAnswers, showResults: true, score: correctCount });
  };

  const handleReset = () => {
    setSelectedAnswers({});
    setShowResults(false);
    setScore(0);
    setCurrentQuestionIndex(0);
    updateState({ answers: {}, showResults: false, score: 0, currentQuestion: 0, resetAt: Date.now() });
  };

  const handleEdit = () => {
    setIsEditing(true);
    setShowResults(false);
  };

  const handleSave = () => {
    setIsEditing(false);
    const updatedData = {
      questions: editableQuestions,
      title: editableTitle,
      level: editableLevel,
      passage: editablePassage,
    };

    if (updateAttributes) {
      updateAttributes(updatedData);
    }

    if (onUpdate) {
      onUpdate(updatedData);
    }
  };

  const handleCancel = () => {
    setIsEditing(false);
    setEditableQuestions([...questions]);
    setEditableTitle(title);
    setEditableLevel(level);
    setEditablePassage(passage || '');
  };

  const updateQuestion = (questionIndex, field, value) => {
    setEditableQuestions(prev =>
      prev.map((q, i) =>
        i === questionIndex ? { ...q, [field]: value } : q
      )
    );
  };

  const updateOption = (questionIndex, optionId, field, value) => {
    setEditableQuestions(prev =>
      prev.map((q, i) =>
        i === questionIndex ? {
          ...q,
          options: q.options.map(opt =>
            opt.id === optionId ? { ...opt, [field]: value } : opt
          )
        } : q
      )
    );
  };

  const getOptionClass = (questionIndex, optionId, isCorrect) => {
    if (!showResults || isEditing) {
      return selectedAnswers[questionIndex] === optionId ? 'selected' : '';
    }
    // Own selections if available, otherwise show other side's answers (teacher watching student)
    const answersForResult = Object.keys(selectedAnswers).length > 0
      ? selectedAnswers
      : (otherState.answers || {});
    if (isCorrect) return 'correct';
    if (answersForResult[questionIndex] === optionId && !isCorrect) return 'incorrect';
    return '';
  };

  const handleNext = () => {
    if (currentQuestionIndex < editableQuestions.length - 1) {
      const next = currentQuestionIndex + 1;
      setCurrentQuestionIndex(next);
      updateState({ currentQuestion: next });
    }
  };

  const handlePrevious = () => {
    if (currentQuestionIndex > 0) {
      const prev = currentQuestionIndex - 1;
      setCurrentQuestionIndex(prev);
      updateState({ currentQuestion: prev });
    }
  };

  const handleAddQuestion = () => {
    const newQuestion = {
      id: `q${Date.now()}`,
      question: '',
      options: [
        { id: 'a', text: '', isCorrect: false },
        { id: 'b', text: '', isCorrect: false },
        { id: 'c', text: '', isCorrect: false },
        { id: 'd', text: '', isCorrect: false }
      ]
    };
    setEditableQuestions(prev => {
      const updated = [...prev, newQuestion];
      setCurrentQuestionIndex(updated.length - 1);
      return updated;
    });
  };

  const handleDeleteQuestion = (questionIndex) => {
    if (editableQuestions.length <= 1) return; // Не удаляем последний вопрос
    
    setEditableQuestions(prev => {
      const updated = prev.filter((_, index) => index !== questionIndex);
      // Корректируем индекс текущего вопроса
      if (currentQuestionIndex >= questionIndex) {
        setCurrentQuestionIndex(Math.max(0, currentQuestionIndex - 1));
      }
      return updated;
    });
  };

  const perDot = 40; // px
  const gap = 8; // px
  const visibleSlots = Math.min(editableQuestions.length, 5);
  const scrollWidth = visibleSlots > 0 ? Math.round((visibleSlots * perDot) + ((visibleSlots - 1) * gap)) : 0;

  return (
    <div className="abcd-exercise abcd-open-style">
      <div className="exercise-header" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
        <h2 className="exercise-title" style={{ margin: 0 }}>
          {isEditing ? (
            <input
              type="text"
              value={editableTitle}
              onChange={(e) => setEditableTitle(e.target.value)}
              className="title-input"
            />
          ) : (
            editableTitle
          )}
        </h2>
        <div style={{ display: 'flex', alignItems: 'center', gap: 0 }}>
          {/* Level is bound to the AI prompt at generation time — hidden in
           * edit mode since relabelling without regeneration is meaningless. */}
          {!isEditing && <span className="level-badge">{editableLevel}</span>}
          {!isEditing && !showResults && !isStudent && (
            <button className="edit-button" onClick={handleEdit} title="Edit Exercise">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#6c757d" strokeWidth="2" strokeLinecap="round">
                <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                <path d="m18.5 2.5 3 3L12 15l-4 1 1-4 9.5-9.5z" />
              </svg>
            </button>
          )}
          {isEditing && (
            <div className="edit-controls">
              <button className="save-button" onClick={handleSave} title="Save">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#28a745" strokeWidth="2" strokeLinecap="round">
                  <circle cx="12" cy="12" r="10" />
                  <polyline points="9,12 11,14 15,10" />
                </svg>
              </button>
              <div className="edit-divider"></div>
              <button className="cancel-button" onClick={handleCancel} title="Cancel">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#dc3545" strokeWidth="2" strokeLinecap="round">
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            </div>
          )}
          {onClose && (
            <div className="close-control">
              <button className="close-button" onClick={onClose} title="Close">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#6c757d" strokeWidth="2" strokeLinecap="round" className="eye-icon">
                  <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" className="eye-open" />
                  <path d="M2 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" className="eye-closed" style={{ opacity: 0 }} />
                  <circle cx="12" cy="12" r="3" className="eye-pupil" />
                </svg>
              </button>
            </div>
          )}
        </div>
      </div>
      {/* Source-text panel. Shown when there IS a passage (or the teacher
       * is in edit mode adding one) — a compact "Read the text" toggle
       * that expands to a scrollable block above the questions, so the
       * student can refer back to what the questions were built from.
       * In edit mode the same block becomes a textarea. */}
      {(isEditing || editablePassage) && (
        <div className={`abcd-passage${passageExpanded || isEditing ? ' is-open' : ''}`}>
          <button
            type="button"
            className="abcd-passage-toggle"
            onClick={() => setPassageExpanded(v => !v)}
            aria-expanded={passageExpanded || isEditing}
          >
            <span className="abcd-passage-toggle-icon" aria-hidden>📖</span>
            <span>{isEditing ? 'Source text' : (passageExpanded ? 'Hide the text' : 'Read the text')}</span>
            {!isEditing && (
              <span className="abcd-passage-toggle-caret" aria-hidden>
                {passageExpanded ? '▲' : '▼'}
              </span>
            )}
          </button>
          {(passageExpanded || isEditing) && (
            isEditing ? (
              <textarea
                className="abcd-passage-textarea"
                value={editablePassage}
                onChange={e => setEditablePassage(e.target.value)}
                placeholder="Paste or edit the source text the questions were generated from…"
                rows={8}
              />
            ) : (
              <div className="abcd-passage-body">
                {editablePassage
                  .split(/\n+/)
                  .map(p => p.trim())
                  .filter(Boolean)
                  .map((para, i) => (
                    <p key={i}>{para}</p>
                  ))}
              </div>
            )
          )}
        </div>
      )}
      <div className="questions-container">
        {/* Показываем только текущий вопрос */}
        {editableQuestions.length > 0 && editableQuestions[currentQuestionIndex] && (
          <div className="abcd-question-card">
            <div className="abcd-question-chip">
              Question {currentQuestionIndex + 1} of {editableQuestions.length}
            </div>
            <div className="abcd-question-text" style={{ position: 'relative' }}>
              {isEditing ? (
                <input
                  type="text"
                  value={editableQuestions[currentQuestionIndex]?.question || ''}
                  onChange={(e) => updateQuestion(currentQuestionIndex, 'question', e.target.value)}
                  className="question-input"
                  placeholder="Enter question..."
                />
              ) : (
                editableQuestions[currentQuestionIndex]?.question || ''
              )}
            </div>
            <div className="abcd-options-list">
              {editableQuestions[currentQuestionIndex]?.options?.map((option, optionIndex) => (
                isEditing ? (
                  <div key={optionIndex} className={`abcd-option-btn ${getOptionClass(currentQuestionIndex, option.id, option.isCorrect)}`}>
                    <div className="option-editor">
                      <input
                        type="radio"
                        name={`correct-${currentQuestionIndex}`}
                        checked={option.isCorrect}
                        onChange={() => {
                          setEditableQuestions(prev => prev.map((q, i) =>
                            i !== currentQuestionIndex ? q : {
                              ...q,
                              options: q.options.map(opt => ({ ...opt, isCorrect: opt.id === option.id }))
                            }
                          ));
                        }}
                        className="correct-radio"
                      />
                      <input
                        type="text"
                        value={option.text}
                        onChange={(e) => updateOption(currentQuestionIndex, option.id, 'text', e.target.value)}
                        className="option-input"
                        placeholder={`Option ${option.id.toUpperCase()}`}
                      />
                    </div>
                  </div>
                ) : (
                  <button
                    key={optionIndex}
                    className={`abcd-option-btn ${getOptionClass(currentQuestionIndex, option.id, option.isCorrect)}${!showResults && otherState.answers?.[currentQuestionIndex] === option.id && selectedAnswers[currentQuestionIndex] !== option.id ? ' other-selected' : ''}`}
                    onClick={() => handleAnswerSelect(currentQuestionIndex, option.id)}
                    disabled={showResults}
                  >
                    <span className="abcd-option-label" aria-hidden>{option.id.toUpperCase()}</span>
                    <span className="abcd-option-text">{option.text}</span>
                    {showResults && option.isCorrect && (
                      <span className="abcd-correct-indicator">✓</span>
                    )}
                  </button>
                )
              ))}
            </div>
            {!isStudent && !isEditing && (() => {
              const correct = editableQuestions[currentQuestionIndex]?.options?.find(o => o.isCorrect);
              if (!correct) return null;
              return (
                <div className="abcd-teacher-hint" role="note" aria-label="Only visible to teacher">
                  <span className="abcd-teacher-hint-badge" aria-hidden>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M17.94 17.94A10.94 10.94 0 0 1 12 20c-7 0-11-8-11-8a19.79 19.79 0 0 1 5.06-5.94M9.9 4.24A10.94 10.94 0 0 1 12 4c7 0 11 8 11 8a19.79 19.79 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/>
                      <line x1="1" y1="1" x2="23" y2="23"/>
                    </svg>
                    Only visible to you
                  </span>
                  <span className="abcd-teacher-hint-answer">
                    Correct: <strong>{correct.id.toUpperCase()}. {correct.text}</strong>
                  </span>
                </div>
              );
            })()}
          </div>
        )}
      </div>
      {/* Навигация по вопросам */}
      {editableQuestions.length > 1 && (
        <div className="navigation-buttons" style={{ alignItems: 'center' }}>
          {currentQuestionIndex > 0 ? (
            <button className="nav-btn prev-btn" onClick={handlePrevious}>
              Previous
            </button>
          ) : <div style={{ width: 80 }}></div>}
          <div className="question-dots" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <div
              ref={dotsScrollRef}
              className="question-dots-scroll"
              style={{
                display: 'flex',
                gap: 8,
                overflowX: 'auto',
                scrollbarWidth: 'thin',
                maxWidth: scrollWidth,
                WebkitMaskImage: (() => {
                  const pad = 28;
                  if (showLeftFade && showRightFade) {
                    return `linear-gradient(to right, rgba(0,0,0,0) 0px, #000 ${pad}px, #000 calc(100% - ${pad}px), rgba(0,0,0,0) 100%)`;
                  }
                  if (showLeftFade && !showRightFade) {
                    return `linear-gradient(to right, rgba(0,0,0,0) 0px, #000 ${pad}px, #000 100%)`;
                  }
                  if (!showLeftFade && showRightFade) {
                    return `linear-gradient(to right, #000 0%, #000 calc(100% - ${pad}px), rgba(0,0,0,0) 100%)`;
                  }
                  return 'none';
                })(),
                maskImage: (() => {
                  const pad = 28;
                  if (showLeftFade && showRightFade) {
                    return `linear-gradient(to right, rgba(0,0,0,0) 0px, #000 ${pad}px, #000 calc(100% - ${pad}px), rgba(0,0,0,0) 100%)`;
                  }
                  if (showLeftFade && !showRightFade) {
                    return `linear-gradient(to right, rgba(0,0,0,0) 0px, #000 ${pad}px, #000 100%)`;
                  }
                  if (!showLeftFade && showRightFade) {
                    return `linear-gradient(to right, #000 0%, #000 calc(100% - ${pad}px), rgba(0,0,0,0) 100%)`;
                  }
                  return 'none';
                })()
              }}
            >
              {editableQuestions.map((q, index) => {
                let dotClass = "dot";
                if (showResults && !isEditing) {
                  const answersForDot = Object.keys(selectedAnswers).length > 0 ? selectedAnswers : (otherState.answers || {});
                  const selected = answersForDot[index];
                  const correctOption = q.options.find(opt => opt.isCorrect)?.id;
                  if (selected) {
                    if (selected === correctOption) {
                      dotClass += " dot-correct";
                    } else {
                      dotClass += " dot-incorrect";
                    }
                  }
                } else if (selectedAnswers[index]) {
                  dotClass += " answered";
                }
                if (index === currentQuestionIndex) dotClass += " active";
                return (
                  <div key={index} style={{ position: 'relative', flex: '0 0 auto' }}>
                    <button
                      className={dotClass}
                      onClick={() => { setCurrentQuestionIndex(index); updateState({ currentQuestion: index }); }}
                    >
                      {index + 1}
                    </button>
                    {isEditing && editableQuestions.length > 1 && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteQuestion(index);
                        }}
                        style={{
                          position: 'absolute',
                          top: 0,
                          right: -6,
                          background: 'rgba(220, 53, 69, 0.8)',
                          border: 'none',
                          cursor: 'pointer',
                          padding: 0,
                          borderRadius: '50%',
                          color: '#fff',
                          fontSize: 8,
                          width: 18,
                          height: 18,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          zIndex: 1000,
                          pointerEvents: 'auto',
                          transition: 'all 0.2s ease',
                          opacity: 0.7,
                          transform: 'scale(0.9)'
                        }}
                        onMouseEnter={e => {
                          e.target.style.background = '#dc3545';
                          e.target.style.opacity = '1';
                          e.target.style.transform = 'scale(1)';
                        }}
                        onMouseLeave={e => {
                          e.target.style.background = 'rgba(220, 53, 69, 0.8)';
                          e.target.style.opacity = '0.7';
                          e.target.style.transform = 'scale(0.9)';
                        }}
                        title="Delete question"
                      >
                        ✕
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
            {/* Mask is applied directly to scroller; overlay fade not needed */}
            {isEditing && (
              <div style={{ position: 'relative', marginLeft: 12 }}>
                <button
                  ref={addBtnRef}
                  className="dot add-dot"
                  style={{
                    background: '#fff',
                    border: '1px dashed #ee5a24',
                    color: '#ee5a24',
                    fontSize: 22,
                    fontWeight: 700,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: 40,
                    height: 40,
                    borderRadius: '50%',
                    cursor: 'pointer',
                    transition: 'all 0.2s'
                  }}
                  onClick={handleAddQuestion}
                  title="Add question"
                  type="button"
                >
                  +
                </button>
              </div>
            )}
          </div>
          {currentQuestionIndex < editableQuestions.length - 1 ? (
            <button className="nav-btn next-btn" onClick={handleNext}>
              Next
            </button>
          ) : <div style={{ width: 80 }}></div>}
        </div>
      )}
      <div className="exercise-controls">
        {!showResults && !isEditing ? (
          <button 
            className="submit-button"
            onClick={handleSubmit}
            disabled={Object.keys(selectedAnswers).length < editableQuestions.length && Object.keys(otherState.answers || {}).length < editableQuestions.length}
          >
            Submit Answers
          </button>
        ) : showResults && !isEditing ? (
          (() => {
            const displayScore = Object.keys(selectedAnswers).length > 0 ? score : (typeof otherState.score === 'number' ? otherState.score : score);
            const total = editableQuestions.length;
            const percent = total ? Math.round((displayScore / total) * 100) : 0;
            const incorrect = total - displayScore;
            const circumference = 2 * Math.PI * 42;
            const dashOffset = circumference * (1 - percent / 100);
            let tier = 'meh';
            let headline;
            let subline;
            if (percent >= 90) { tier = 'excellent'; headline = 'Outstanding!'; subline = isStudent ? 'You nailed it.' : 'Student nailed it.'; }
            else if (percent >= 70) { tier = 'great'; headline = 'Great job!'; subline = isStudent ? 'Solid performance.' : 'Solid performance.'; }
            else if (percent >= 50) { tier = 'good'; headline = 'Good work.'; subline = isStudent ? 'Keep going — you’re close.' : 'Getting there.'; }
            else { tier = 'practice'; headline = 'Keep practicing'; subline = isStudent ? 'You’ll get it — try again.' : 'Review and try again.'; }
            return (
              <div className={`results-container abcd-results tier-${tier}`}>
                <div className="abcd-results-ring-wrap">
                  <svg viewBox="0 0 100 100" className="abcd-results-ring" aria-hidden>
                    <circle cx="50" cy="50" r="42" className="abcd-results-ring-track" />
                    <circle
                      cx="50"
                      cy="50"
                      r="42"
                      className="abcd-results-ring-progress"
                      style={{ strokeDasharray: circumference, strokeDashoffset: dashOffset }}
                    />
                  </svg>
                  <div className="abcd-results-ring-center">
                    <div className="abcd-results-percent">{percent}%</div>
                    <div className="abcd-results-percent-label">score</div>
                  </div>
                </div>
                <div className="abcd-results-headline">{headline}</div>
                <div className="abcd-results-subline">{subline}</div>
                <div className="abcd-results-stats">
                  <div className="abcd-results-stat is-correct">
                    <span className="abcd-results-stat-icon" aria-hidden>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="20 6 9 17 4 12"/>
                      </svg>
                    </span>
                    <span className="abcd-results-stat-value">{displayScore}</span>
                    <span className="abcd-results-stat-label">correct</span>
                  </div>
                  <div className="abcd-results-stats-divider" aria-hidden />
                  <div className="abcd-results-stat is-incorrect">
                    <span className="abcd-results-stat-icon" aria-hidden>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                        <line x1="18" y1="6" x2="6" y2="18"/>
                        <line x1="6" y1="6" x2="18" y2="18"/>
                      </svg>
                    </span>
                    <span className="abcd-results-stat-value">{incorrect}</span>
                    <span className="abcd-results-stat-label">incorrect</span>
                  </div>
                </div>
                <button className="abcd-results-cta" onClick={handleReset}>
                  <span>Try Again</span>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                    <polyline points="23 4 23 10 17 10"/>
                    <path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"/>
                  </svg>
                </button>
              </div>
            );
          })()
        ) : null}
      </div>
    </div>
  );
};

export default ABCDExercise; 